import {
  AcceptanceButton,
  AcceptanceFooter,
  AcceptanceTextField,
} from "layouts/AcceptancePage/components";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import clsx from "clsx";
import useMutationAddBankDetails from "../../hooks/useMutattionAddBankDetails";

type Props = {
  setActualStep: (num: number) => void;
  brand: string;
  personKey: string;
  ddiUrl: string;
};

const useStyles = makeStyles(theme => ({
  subText: {
    fontWeight: 400,
    "&.bsm": {
      fontWeight: 300,
    },
    fontSize: "18px",
    lineHeight: "27px",
    color: "#2B2D32",
    marginBottom: "3%",
    "@media (max-width: 767px)": {
      fontSize: "16px",
    },
  },
  sectionStyle: {
    marginBottom: "72px",
  },
  sectionDebitInstruction: {
    background: "#FFFFFF",
    borderBottom: "1px solid #E3E2E3",
    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.23)",
    borderRadius: "4px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 64px 16px 16px",
    marginBottom: "24px",
    maxWidth: "537px",
    "@media (max-width: 767px)": {
      flexDirection: "column",
      alignItems: "flex-start",
      padding: "20px 24px",
    },
  },
  titleDebitInstruction: {
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: "0.3375px",
    color: "#2B2929",
    margin: "12px 0",
    textTransform: "capitalize",
    [theme.breakpoints.up("sm")]: {
      fontSize: "18px",
      lineHeight: "27px",
    },
  },
  formDebitInstruction: {
    [theme.breakpoints.up("sm")]: {
      width: "302px",
    },
  },
  sortCodeInput: {
    maxWidth: "144px",
  },
  boldLink: {
    fontWeight: 700,
    textDecoration: "underline !important",
    "&.bsm": {
      fontWeight: 300,
      textDecoration: "none !important",
    },
  },
  formControlLabel: {
    marginLeft: "0px",
    display: "flex",
    alignItems: "flex-start",
    "& .MuiCheckbox-root": {
      color: "#2B2929",
      paddingLeft: 0,
      paddingTop: "1px",
    },
    "& .MuiFormControlLabel-label": {
      fontWeight: 700,
      fontSize: "20px",
      letterSpacing: "0.3375px",
      color: "#2B2929",
    },
    "&.bsm .MuiFormControlLabel-label": {
      fontWeight: 300,
    },
    "& .MuiCheckbox-colorPrimary.Mui-checked": {
      color: "#2B2929",
    },
  },
  titleBsm: {
    fontFamily: "Poppins !important",
  },
  titleAa: {
    fontFamily: "New Transport AA !important",
  },
  linkCancel: {
    fontFamily: "New Transport AA !important",
    fontStyle: "normal",
    fontWeight: 400,
    "&.bsm": {
      fontWeight: 300,
    },
    fontSize: "14px",
    lineHeight: "125%",
    letterSpacing: "-0.001em",
    color: "#2B2D32",
    marginTop: "3%",
  },
  divFooter: {
    clear: "both",
    width: "100%",
  },
}));

const PaymentDetailsComponent: React.FC<Props> = ({
  setActualStep,
  brand,
  personKey,
  ddiUrl,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [nameOnAccountForm, setNameOnAccountForm] = useState("");
  const [sortCodeForm, setSortCodeForm] = useState("");
  const [accountNumberForm, setAccountNumberForm] = useState("");

  const [isConfirmationChecked, setIsConfirmationChecked] = useState(false);

  const [nameAccountError, setNameAccountError] = useState<string | null>(null);
  const [accountNumberError, setAccountNumberError] = useState<string | null>(
    null,
  );
  const [sortCodeError, setSortCodeError] = useState<string | null>(null);

  const [isNameAccountVisited, setNameAccountVisited] = useState(false);
  const [isAccountNumberVisited, setAccountNumberVisited] = useState(false);
  const [isSortCodeVisited, setSortCodeVisited] = useState(false);

  const validateNameAccountField = () => {
    // setNameAccountValid(!!nameOnAccountForm.trim().length);
    if (nameOnAccountForm.trim().length < 1)
      return setNameAccountError("Please enter the account holder name");
    setNameAccountError(null);
  };
  const validateAccountNumberField = () => {
    if (/[^0-9]/.test(accountNumberForm.trim()))
      return setAccountNumberError("Must be a valid account number");
    if (accountNumberForm.trim().length !== 8)
      return setAccountNumberError("Please enter the account number");
    setAccountNumberError(null);
  };
  const validateSortCodeField = () => {
    if (/[^0-9]/.test(sortCodeForm.trim()))
      return setSortCodeError("Must be a valid sort code");
    if (sortCodeForm.trim().length !== 6)
      return setSortCodeError("Please enter the sort code");
    setSortCodeError(null);
  };

  const leaveNameAccountField = () => {
    setNameAccountVisited(true);
  };
  const leaveAccountNumberField = () => {
    setAccountNumberVisited(true);
  };
  const leaveSortCodeField = () => {
    setSortCodeVisited(true);
  };

  useEffect(() => {
    if (isNameAccountVisited || isConfirmationChecked)
      validateNameAccountField();
    if (isAccountNumberVisited || isConfirmationChecked)
      validateAccountNumberField();
    if (isSortCodeVisited || isConfirmationChecked) validateSortCodeField();
  }, [
    nameOnAccountForm,
    sortCodeForm,
    accountNumberForm,
    isNameAccountVisited,
    isAccountNumberVisited,
    isSortCodeVisited,
    isConfirmationChecked,
  ]);

  const isDisabledContinueBtn = !(
    isConfirmationChecked &&
    !nameAccountError &&
    !accountNumberError &&
    !sortCodeError
  );

  const manageNextStep = () => {
    checkBankingDataFunc(
      personKey,
      accountNumberForm,
      sortCodeForm,
      nameOnAccountForm,
    );
  };

  const [
    checkBankingData,
    { loading: isLoadingContinueBtn },
  ] = useMutationAddBankDetails(
    personKey,
    accountNumberForm,
    sortCodeForm,
    nameOnAccountForm,
    {
      onCompleted: () => {
        setActualStep(1);
      },
    },
    {
      onError: () => {
        enqueueSnackbar(
          "Error saving bank details, please try again or contact support",
          {
            variant: "error",
            autoHideDuration: 4000,
          },
        );
      },
    },
  );

  const checkBankingDataFunc = async (
    personKey: string,
    accountNumber: string,
    sortCode: string,
    nameOnAccount: string,
  ) => {
    checkBankingData({
      variables: {
        personKey: personKey,
        accountNumber: accountNumber,
        sortCode: sortCode,
        nameOnAccount: nameOnAccount,
      },
    });
  };

  const classes = useStyles();
  return (
    <div className={classes.sectionStyle}>
      <p
        className={clsx(classes.subText, {
          bsm: brand === "BSM",
        })}
      >
        Your weekly franchise fee will be collected via Direct Debit. Please
        read your Direct Debit Instruction document and enter your details below
        to set up your payments.
      </p>
      <div className={classes.sectionDebitInstruction}>
        <Typography
          className={clsx(classes.titleDebitInstruction, {
            [classes.titleBsm]: brand === "BSM",
            [classes.titleAa]: brand !== "BSM",
          })}
        >
          Direct Debit Instruction
        </Typography>
        <AcceptanceButton
          brand={brand}
          onClick={() => window.open(ddiUrl, "_blank")}
        >
          Read
        </AcceptanceButton>
      </div>
      <div>
        <p
          className={clsx(classes.subText, {
            bsm: brand === "BSM",
          })}
        >
          Please enter your Direct Debit details below
        </p>
        <form className={classes.formDebitInstruction}>
          <Box>
            <AcceptanceTextField
              id="nameAccount"
              label="Name on account"
              fullWidth
              onChange={e => setNameOnAccountForm(e.target.value)}
              onBlur={leaveNameAccountField}
              error={!!nameAccountError}
              helperText={nameAccountError}
            />
          </Box>
          <Box marginTop={3}>
            <AcceptanceTextField
              id="accountNumber"
              label="Account number"
              fullWidth
              onChange={e => setAccountNumberForm(e.target.value)}
              onBlur={leaveAccountNumberField}
              error={!!accountNumberError}
              helperText={accountNumberError}
            />
          </Box>
          <Box marginTop={3} marginBottom={3}>
            <AcceptanceTextField
              className={classes.sortCodeInput}
              id="sortCode"
              label="Sort code"
              fullWidth
              onChange={e => setSortCodeForm(e.target.value)}
              onBlur={leaveSortCodeField}
              error={!!sortCodeError}
              helperText={sortCodeError}
            />
          </Box>
        </form>
        <div>
          <FormControlLabel
            control={
              <Checkbox
                checked={isConfirmationChecked}
                onChange={() =>
                  setIsConfirmationChecked(!isConfirmationChecked)
                }
                name="checkedB"
                color="primary"
              />
            }
            className={clsx(classes.formControlLabel, {
              bsm: brand === "BSM",
            })}
            label="I confirm I am the account holder and the only person required to authorise Direct Debits"
          />
          <Box marginTop="48px">
            <AcceptanceButton
              disabled={isDisabledContinueBtn || isLoadingContinueBtn}
              loading={isLoadingContinueBtn}
              onClick={() => manageNextStep()}
              brand={brand}
            >
              Continue to documents
            </AcceptanceButton>
          </Box>
          <div className={classes.divFooter}>
            <Typography
              className={clsx(classes.linkCancel, {
                bsm: brand === "BSM",
              })}
            >
              If you wish to cancel this application, you can close this window
              or&nbsp;
              <a
                className={clsx(classes.boldLink, {
                  bsm: brand === "BSM",
                })}
                href={
                  brand === "BSM"
                    ? "https://www.bsm.co.uk/"
                    : "https://www.theaa.com/driving-school"
                }
              >
                exit to the driving school homepage
              </a>
            </Typography>
          </div>
        </div>

        <AcceptanceFooter brand={brand} />
      </div>
    </div>
  );
};

export default PaymentDetailsComponent;
